import React, { useEffect, useState } from 'react';
import { Card, Avatar, Tag, Button, Row, Col, Typography, Spin, Modal, Divider, Timeline, Badge } from 'antd';

import {
    UserOutlined, PhoneOutlined, MailOutlined, CalendarOutlined,
    ClockCircleOutlined, StarFilled, ArrowRightOutlined, InfoCircleOutlined,
    EnvironmentOutlined, MedicineBoxOutlined, IdcardOutlined,
    BookOutlined, CloseOutlined,
    LinkOutlined,
    Loading3QuartersOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import drEasyApi from '../../Api/Customapi';
import { useDoctorQuery } from '../../hooks/useDoctorQuery';
import { useDoctorContext } from '../../context/DoctorContext';
import { MdOutlineEmail } from 'react-icons/md';
import NeedAuthModal from '../Modals/NeedAuthModal';
import GuestBookingModal from '../Modals/GuestInformationModal';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getImg } from '../../Api/getImg';
import { useLanguage } from '../../context/LanguageContext';
import i18n from '../../i18n';
import { slug } from '../../utils/store';
import LoginModal from '../Modals/LoginModal';

const { Title, Text, Paragraph } = Typography;

const containerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.1,
        },
    },
};

const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] },
    },
};

const getDayName = (dayNumber, t) => {
    const days = [
        t('common.days.sunday'),
        t('common.days.monday'),
        t('common.days.tuesday'),
        t('common.days.wednesday'),
        t('common.days.thursday'),
        t('common.days.friday'),
        t('common.days.saturday')
    ];
    return days[dayNumber] || t('common.days.unknown');
};

const getDayNameShort = (dayNumber, t) => {
    const days = [
        t('common.days.sunday').substring(0, 3),
        t('common.days.monday').substring(0, 3),
        t('common.days.tuesday').substring(0, 3),
        t('common.days.wednesday').substring(0, 3),
        t('common.days.thursday').substring(0, 3),
        t('common.days.friday').substring(0, 3),
        t('common.days.saturday').substring(0, 3)
    ];
    return days[dayNumber];
};

const formatTime = (timeString) => {
    const time = new Date(`1970-01-01T${timeString}`);
    return time.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

const getWorkingDays = (workingDays, t) => {
    return workingDays
        .filter(day => day.isWorking && day.availabilityHours.length > 0)
        .map(day => getDayNameShort(day.dayOfWeek, t))
        .slice(0, 4);
};

// Doctor Detail Modal Component
const DoctorDetailModal = ({ doctor, visible, onClose, onBookAppointment, index }) => {
    const { t } = useLanguage();
    const gradients = [
        'from-blue-400 to-blue-600',
        'from-purple-400 to-purple-600',
        'from-emerald-400 to-emerald-600',
        'from-orange-400 to-orange-600',
        'from-pink-400 to-pink-600',
        'from-indigo-400 to-indigo-600'
    ];

    const avatarGradient = gradients[index % gradients.length];
    const workingDays = doctor.clinics[0]?.workingDays || [];
    const navigate = useNavigate();

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            footer={null}
            width="95%"
            style={{ maxWidth: '900px' }}
            centered
            className="doctor-detail-modal"
            closeIcon={<CloseOutlined className="text-gray-500 hover:text-gray-700 text-lg sm:text-xl" />}
            bodyStyle={{ padding: 0 }}
        >
            <div className="relative overflow-hidden">
                {/* Header Section with Gradient Background */}
                <div
                    className="relative p-4 sm:p-6 lg:p-8 text-white"
                    style={{
                        background: 'linear-gradient(135deg, #7A5C1E 0%, #1F6F7D 50%, #C9A84C 100%)',
                    }}
                >
                    <div className="absolute top-0 right-0 w-20 h-20 sm:w-32 sm:h-32 lg:w-40 lg:h-40 bg-white/10 rounded-full -translate-y-10 translate-x-10 sm:-translate-y-16 sm:translate-x-16 lg:-translate-y-20 lg:translate-x-20"></div>
                    <div className="absolute bottom-0 left-0 w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 bg-white/10 rounded-full translate-y-8 -translate-x-8 sm:translate-y-12 sm:-translate-x-12 lg:translate-y-16 lg:-translate-x-16"></div>

                    <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
                        {/* Avatar */}
                        <div className="relative">
                            <div className='w-36 h-36 rounded-full'>
                                <img src={getImg(doctor.profilePhoto)} className='rounded-full h-36 w-36 object-cover border border-1 border-primary' />
                            </div>
                            <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 bg-white rounded-full p-1.5 sm:p-2 shadow-lg">
                                <div className="flex items-center space-x-1">
                                    <StarFilled className="text-yellow-400 text-xs sm:text-sm" />
                                    <span className="text-xs sm:text-sm font-bold text-gray-700">{t('doctors.card.rating')}</span>
                                </div>
                            </div>
                        </div>

                        {/* Basic Info */}
                        <div className="flex-1 text-center sm:text-left">
                            <Title level={window.innerWidth < 640 ? 3 : 2} className="text-white mb-2 font-bold">
                                {t("common.dr")} {doctor.fullName}
                            </Title>
                            <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-white/20 rounded-full mb-3 sm:mb-4">
                                <MedicineBoxOutlined className="mr-2 text-sm sm:text-base" />
                                <span className="font-medium text-sm sm:text-base">{doctor.specialization}</span>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
                                <Badge
                                    status="processing"
                                    color='white'
                                    text={workingDays.length > 0 ? t('doctors.card.available') : t('doctors.card.unavailable')}
                                    className="text-white text-sm"
                                />
                                <span className="text-green/80 hidden sm:inline">•</span>
                                <span className="text-white/80 text-sm">{t('doctors.modal.licensedProfessional')}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="p-4 sm:p-6 lg:p-8">
                    <Row gutter={[16, 24]} className="lg:gutter-24">
                        {/* Left Column */}
                        <Col xs={24} lg={12}>
                            {/* Contact Information */}
                            <div className="mb-6 sm:mb-8">
                                <Title level={4} className="flex items-center mb-3 sm:mb-4 text-gray-800 text-base sm:text-lg">
                                    <IdcardOutlined className="mr-2 text-[#7A5C1E]" />
                                    {t('doctors.modal.contactInformation')}
                                </Title>

                                <div className="space-y-3">
                                    {/* <div className="flex items-center p-2.5 sm:p-3 bg-gray-50 rounded-lg">
                                        <PhoneOutlined className="text-[#7A5C1E] mr-2 sm:mr-3 text-sm sm:text-base" />
                                        <div>
                                            <Text className="text-xs text-gray-500 block">{t('doctors.card.phone')}</Text>
                                            <Text className="font-semibold text-sm sm:text-base">{doctor.phoneNumber}</Text>
                                        </div>
                                    </div> */}

                                    <div className="flex items-center p-2.5 sm:p-3 bg-gray-50 rounded-lg">
                                        <MailOutlined className="text-primary mr-2 sm:mr-3 text-sm sm:text-base" />
                                        <div className="flex-1 min-w-0">
                                            <Text className="text-xs text-gray-500 block">{t('doctors.card.email')}</Text>
                                            <Text className="font-semibold truncate text-sm sm:text-base">{doctor.email}</Text>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Professional Details */}
                            <div className="mb-6 sm:mb-8">
                                <Title level={4} className="flex items-center mb-3 sm:mb-4 text-gray-800 text-base sm:text-lg">
                                    {t('doctors.modal.professionalDetails')}
                                </Title>

                                <div className="space-y-4">
                                    <div className="p-3 sm:p-4 bg-gradient-to-r from-[#7A5C1E]/10 to-primary/10 rounded-lg">
                                        <Text className="text-sm text-gray-600 block mb-1">{t('doctors.modal.specialization')}</Text>
                                        <Text className="font-bold text-[#C9A84C] text-sm sm:text-base">{doctor.specialization}</Text>
                                    </div>

                                    <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                                        <Text className="text-sm text-gray-600 block mb-1">{t('doctors.modal.experience')}</Text>
                                        <Text className="font-semibold text-sm sm:text-base">{t('doctors.modal.yearsExperience')}</Text>
                                    </div>
                                </div>
                            </div>
                        </Col>

                        {/* Right Column */}
                        <Col xs={24} lg={12}>
                            {/* Clinic Information */}
                            {doctor.clinics[0] && (
                                <div className="mb-6 sm:mb-8">
                                    <Title level={4} className="flex items-center mb-3 sm:mb-4 text-gray-800 text-base sm:text-lg">
                                        <EnvironmentOutlined className="mr-2 text-[#7A5C1E]" />
                                        {t('doctors.modal.clinicInformation')}
                                    </Title>

                                    <div className="p-4 sm:p-6 bg-gradient-to-br from-[#EBF8FA] to-white rounded-xl border border-[#7A5C1E]/20">
                                        <Title level={5} className="text-[#C9A84C] mb-2 sm:mb-3 text-sm sm:text-base">
                                            {doctor.clinics[0].clinicName}
                                        </Title>

                                        <Paragraph className="text-gray-600 mb-3 sm:mb-4 text-sm">
                                            {doctor.clinics[0].detailedAddress}
                                        </Paragraph>

                                        <div className="flex items-center space-x-2 text-sm text-green-600">
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            <span>{t('doctors.modal.currentlyOperating')}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Working Schedule */}
                            <div>
                                <Title level={4} className="flex items-center mb-3 sm:mb-4 text-gray-800 text-base sm:text-lg">
                                    <CalendarOutlined className="mr-2 text-[#7A5C1E]" />
                                    {t('doctors.modal.workingSchedule')}
                                </Title>
                                {workingDays.length > 0 ? (
                                    <div className="space-y-2 sm:space-y-3">
                                        {workingDays.slice(0, window.innerWidth < 640 ? 3 : workingDays.length).map((day, index) => (
                                            <div
                                                key={index}
                                                className={`p-3 sm:p-4 rounded-lg border ${day.isWorking
                                                    ? 'bg-green-50 border-green-200'
                                                    : 'bg-gray-50 border-gray-200'
                                                    }`}
                                            >
                                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
                                                    <div className="flex items-center gap-10">
                                                        <span className="font-semibold text-gray-800 w-16 sm:w-28 text-sm sm:text-base">
                                                            {getDayName(day.dayOfWeek, t)}
                                                        </span>
                                                        {day.isWorking ? (
                                                            <Badge status="success" text={t('doctors.modal.available')} />
                                                        ) : (
                                                            <Badge status="default" text={t('doctors.modal.closed')} />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div>
                                        {t('doctors.modal.noAvailability')}
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Divider />

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                        <Button
                            type=""
                            size={window.innerWidth < 640 ? "middle" : "large"}
                            className="h-10 sm:h-12 font-bold text-xs sm:text-sm bg-primary hover:bg-secondary text-white rounded-lg sm:rounded-xl border-0 shadow-lg relative overflow-hidden group"
                            style={{
                                boxShadow: '0 8px 25px -5px rgba(41, 142, 159, 0.4)'
                            }}
                            onClick={() => onBookAppointment(doctor)}
                        >
                            <span className="flex items-center justify-center space-x-2 relative z-10">
                                <CalendarOutlined />
                                <span>{t('doctors.modal.bookAppointment')}</span>
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                        </Button>
                        <Link
                            type=''
                            to={`https://dreasey.com/${doctor.slug}`}
                            size=""
                            className="w-full sm:w-auto px-6 sm:px-8 py-2 h-10 sm:h-10 font-semibold rounded-xl border-2 border-[#7A5C1E] text-[#7A5C1E] hover:bg-[#7A5C1E] hover:text-white text-sm sm:text-base"
                        >
                            <LinkOutlined className="mr-2" />
                            {t('doctors.modal.visitDoctorSite')}
                        </Link>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

const DoctorCard = ({ doctor, index, onBookAppointment }) => {
    const { t, isRTL } = useLanguage();
    const [modalVisible, setModalVisible] = useState(false);
    const workingDays = doctor.clinics[0]?.workingDays || [];
    const availableDays = getWorkingDays(workingDays, t);
    const firstAvailableHour = workingDays.find(day => day.isWorking && day.availabilityHours.length > 0)?.availabilityHours[0];
    const navigate = useNavigate();
    const { slug } = useParams();

    const gradients = [
        'from-primary to-secondary',
        'from-primary to-secondary',
    
    ];

    const avatarGradient = gradients[index % gradients.length];
    const hasWorkingDays = workingDays.some(day => day.isWorking && day.availabilityHours.length > 0);

    return (
        <>
            <motion.div
                variants={cardVariants}
                className="group h-full"
                whileHover={{ y: window.innerWidth >= 1024 ? -10 : 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <div className="relative h-full overflow-hidden">
                    {/* Glassmorphism Card */}
                    <div
                        className="relative h-full p-4 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl backdrop-blur-xl border border-orange-300 shadow-2xl"
                        style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.2)'
                        }}
                    >
                        {/* Floating Elements - Hidden on mobile */}
                        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 w-12 h-12 sm:w-20 sm:h-20 bg-gradient-to-r from-[#7A5C1E]/10 to-primary/10 rounded-full blur-xl hidden sm:block"></div>
                        <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-r from-primary/10 to-[#7A5C1E]/10 rounded-full blur-xl hidden sm:block"></div>

                        {/* Status Badge */}
                        <div className={`absolute top-3 ${isRTL ? "left-3 sm:left-5" : "right-3 sm:right-5"}  sm:top-6  z-40`}>
                            <div className={`flex items-center space-x-1 ${hasWorkingDays ? "bg-emerald-500/10 backdrop-blur-sm" : "bg-gray-200"} px-2 py-1 sm:px-3 sm:py-1 rounded-full border border-emerald-200/30`}>
                                <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 ${hasWorkingDays ? "bg-emerald-500" : "bg-gray-300"} rounded-full animate-pulse`}></div>
                                <span className="text-emerald-700 text-xs font-medium ">
                                    {hasWorkingDays ? t('doctors.card.available') : t('doctors.card.unavailable')}
                                </span>
                            </div>
                        </div>

                        {/* Avatar Section */}
                        <div className="flex flex-col items-center mb-6 sm:mb-8 relative z-10">
                            <div className="relative">
                                <div className='w-20 h-20 md:w-36 md:h-36 rounded-full'>
                                    <img src={doctor.profilePhoto ? getImg(doctor.profilePhoto) : "/anonymous.png"} className='rounded-full h-20 w-20 md:h-36 md:w-36 object-cover border border-1 border-primary' />
                                </div>
                                {/* Rating Stars */}
                                <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 bg-white rounded-full p-1.5 sm:p-2 shadow-lg">
                                    <div className="flex items-center space-x-1">
                                        <StarFilled className="text-yellow-400 text-xs sm:text-sm" />
                                        <span className="text-xs font-bold text-gray-700">{t('doctors.card.rating')}</span>
                                    </div>
                                </div>
                            </div>

                            <motion.div
                                className="text-center mt-4 sm:mt-6"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                <Title
                                    level={window.innerWidth < 640 ? 5 : 4}
                                    className="mb-2 bg-gradient-to-r from-[#C9A84C] to-[#7A5C1E] bg-clip-text text-transparent font-bold"
                                    style={{ fontSize: window.innerWidth < 640 ? '1rem' : '1.2rem', lineHeight: '1.2' }}
                                >
                                    {t("common.dr")}  {doctor.fullName}
                                </Title>

                                <div className="inline-flex items-center px-2.5 sm:px-3 py-1 bg-goldy-hover rounded-full shadow-lg">
                                    <span className="text-white font-medium text-xs">{doctor.specialization}</span>
                                </div>
                            </motion.div>
                        </div>

                        {/* Quick Info */}
                        <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                            {/* Phone */}
                            {/* <div className="flex items-center p-2.5 sm:p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100/50">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 mx-2 bg-goldy rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                                    <PhoneOutlined className="text-white text-xs " />
                                </div>
                                <div>
                                    <Text className="text-xs text-gray-500 block">{t('doctors.card.phone')}</Text>
                                    <Text className="font-semibold text-gray-800 text-xs sm:text-sm">{doctor.phoneNumber}</Text>
                                </div>
                            </div> */}
                            {/* <div className="flex items-center p-2.5 sm:p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100/50">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 mx-2 bg-goldy rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                                    <MdOutlineEmail className="text-white text-xs sm:text-sm" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <Text className="text-xs text-gray-500 block">{t('doctors.card.email')}</Text>
                                    <Text className="font-semibold text-gray-800 text-xs sm:text-sm truncate">{doctor.email}</Text>
                                </div>
                            </div> */}
                        </div>

                        <div className="space-y-2 sm:space-y-3">
                            <motion.div whileHover={{ scale: window.innerWidth >= 1024 ? 1.02 : 1 }} whileTap={{ scale: 0.98 }}>
                                <Button
                                    type=""
                                    block
                                    onClick={() => onBookAppointment(doctor)}
                                    size={window.innerWidth < 640 ? "middle" : "large"}
                                    className="h-10 sm:h-12 font-bold text-xs sm:text-sm bg-goldy text-white rounded-lg sm:rounded-xl border-0 shadow-lg relative overflow-hidden group"
                                    style={{
                                        boxShadow: '0 8px 25px -5px rgba(41, 142, 159, 0.4)'
                                    }}
                                >
                                    <span className="flex items-center justify-center md:space-x-2 text-sm md:text-base relative z-10 ">
                                        <CalendarOutlined className='text-xs md:text-base mx-1 md:mx-2' />
                                        <span className="text-xs md:text-base">{t('doctors.card.bookAppointment')}</span>
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                                </Button>
                            </motion.div>

                            <Button
                                type=''
                                block
                                size={window.innerWidth < 640 ? "middle" : "large"}
                                className="h-10 sm:h-12 font-semibold text-xs sm:text-sm rounded-lg sm:rounded-xl border-1 border-primary text-primary transition-all duration-300"
                                onClick={() => navigate(`/doctor-details`, { state: { doctor } })}
                            >
                                <InfoCircleOutlined className="mr-2" />
                                <span className="hidden sm:inline">{t('doctors.card.viewDetails')}</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Doctor Detail Modal */}
            <DoctorDetailModal
                onBookAppointment={onBookAppointment}
                doctor={doctor}
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                index={index}
            />
        </>
    );
};

export default function OurDoctors() {
    const { t, currentLanguage } = useLanguage();
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const { data: doctor } = useDoctorQuery();
    const { setHasServices, setHasDoctors } = useDoctorContext();
    const navigate = useNavigate();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isGuestModalVisible, setIsGuestModalVisible] = useState(false);
    const [pendingDoctor, setPendingDoctor] = useState(null);
    const [overView, setOverview] = useState(null);
 const [pendingRoute, setPendingRoute] = useState(null);
 const [isAppoinments, setIsAppoinments] = useState(false)
    const url = window.location.href;
    const currentPath = url.substring(url.lastIndexOf('/') + 1);
    const isOurDoctorsPage = currentPath === "our-doctors";
const [loginModal, setLoginModal] = useState(false);

    // Check if user is authorized
    const isAuthorized =
        !!localStorage.getItem(`userTkn-${slug}`) ||
        !!sessionStorage.getItem(`userTkn-${slug}`);

  const handleDoctorBooking = (doctor) => {
    if (!isAuthorized) {
         setPendingDoctor(doctor);
         setPendingRoute(null); // doctor booking uses pendingDoctor, not a route
         setIsAppoinments(false);
         setIsModalVisible(true);
     } else {
         navigateToBooking(doctor);
     }
 };

   const navigateToBooking = (doctor) => {
    const clinicId = doctor.clinics[0]?.clinicId;
    if (clinicId) {
        navigate(`/book-appointment?DoctorId=${doctor.doctorId}&ClinicId=${clinicId}&showOptions=false`);
    } else {
        navigate(`/book-appointment`);
    }
};

    const handleGuestBooking = () => {
        setIsModalVisible(false);
        setIsGuestModalVisible(true);
    };

    const handleGuestInfoSubmit = (guestInfo) => {
        setIsGuestModalVisible(false);
        sessionStorage.setItem(`guestInfo-${slug}`, JSON.stringify(guestInfo));
        sessionStorage.setItem(`isGuestBooking-${slug}`, 'true');
        if (pendingDoctor) {
            navigateToBooking(pendingDoctor);
        }
    };

    const availableDoctors = doctors.filter(doc =>
        doc.clinics[0]?.workingDays?.some(day => day.isWorking && day.availabilityHours.length > 0)
    );

    // Determine display logic based on current path
    const shouldShowSeeMore = !isOurDoctorsPage && availableDoctors.length > 3;
    const displayedDoctors = isOurDoctorsPage ? availableDoctors : (shouldShowSeeMore ? availableDoctors.slice(0, 3) : availableDoctors);

    useEffect(() => {
        const ftchOverView = async () => {
            try {
                const res = await drEasyApi.get('/api/Centers/GetCenterOverview', {
                    params: { CenterId: doctor?.id },
                });
                setOverview(res.data);
            } catch (err) {
                console.error('Failed to load center overview', err);
            }
        };
        const fetchDoctors = async () => {
            try {
                const res = await drEasyApi.get('/api/Centers/GetCenterDoctors', {
                    params: { CenterId: doctor?.id },
                });

                const doctorsData = res.data?.doctors || res.data || [];
                setDoctors(doctorsData);

                if (doctorsData.length > 0) {
                    setHasDoctors(true);
                }
            } catch (err) {
                console.error('Failed to load doctors', err);
            } finally {
                setLoading(false);
            }
        };

        if (doctor?.id) {
            fetchDoctors();
            ftchOverView();
        }
    }, [doctor?.id, setHasServices, currentLanguage]);

    if (loading) {
        return (
            <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #F7FCFD 0%, #EBF8FA 100%)' }}>
                <div className="container mx-auto px-4">
                    <div className="flex justify-center items-center h-32 sm:h-48 lg:h-64">
                        <Loading3QuartersOutlined color='green' className='animate-spin ' />
                    </div>
                </div>
            </section>
        );
    }

    if (!loading && displayedDoctors.length === 0) {
        setHasDoctors(false)
        return null;
    }

    return (
        <section className={`relative py-3 sm:py-4 overflow-hidden bg-cardColor ${isOurDoctorsPage ? "pb-32" : "pb-10 "} min-h-screen flex flex-col justify-center items-center mx-auto  `}>
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-20 -right-20 sm:-top-32 sm:-right-32 lg:-top-40 lg:-right-40 w-48 h-48 sm:w-72 sm:h-72 lg:w-96 lg:h-96 bg-gradient-to-br from-[#7A5C1E]/10 to-primary/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-20 -left-20 sm:-bottom-32 sm:-left-32 lg:-bottom-40 lg:-left-40 w-40 h-40 sm:w-60 sm:h-60 lg:w-80 lg:h-80 bg-gradient-to-tr from-primary/10 to-[#7A5C1E]/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 sm:w-48 sm:h-48 lg:w-60 lg:h-60 bg-gradient-to-r from-[#7A5C1E]/5 to-primary/5 rounded-full blur-3xl"></div>
            </div>

            <div className="container mx-auto sm:px-6  md:pb-10 relative z-10  px-4 md:px-8 lg:px-16 max-w-7xl">
                {/* Hero Section Header - Only show on non-our-doctors page */}
                {!isOurDoctorsPage && (
                    <motion.div
                        className="text-center mb-12 sm:mb-16 "
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <motion.div
                            className="inline-flex items-center justify-center mb-6 "
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, duration: 0.6, type: "spring" }}
                        >
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.8 }}
                        >
                            <Title
                                level={1}
                                className="mb-4 goldy text-2xl md:text-4xl  leading-tight"
                                style={{
                                    color: "white",
                                    fontSize: window.innerWidth < 640 ? '1.5rem' : window.innerWidth < 1024 ? '2rem' : '2.5rem',
                                    textShadow: 'none'
                                }}
                            >
        <h2 className=" text-2xl md:text-4xl font-bold text-center goldy flex items-center justify-center gap-3">
                                    {t('doctors.title').split(' ').slice(-2).join(' ')}
                                </h2>
                            </Title>

                                   <p className="mt-3 text-textPrimary/70 text-sm md:text-base max-w-2xl mx-auto">

                                {t('doctors.subtitle')}
                            </p>

                            {/* Stats */}
                            <div className="grid grid-cols-3  justify-center items-center space-y-6 sm:space-y-0 sm:space-x-8 lg:space-x-16 mt-6 ">
                                <div className="text-center">
                                    <div className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-primary mb-1">
                                        {overView?.totalDoctors || 1}+</div>
                                    <div className="text-xs sm:text-sm text-gray-400">
                                        {t('doctors.stats.doctors')}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-primary mb-1">
                                        {overView?.totalClinics || 1}+</div>
                                    <div className="text-xs sm:text-sm text-gray-400">
                                        {t('doctors.stats.clinics')}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-primary mb-1">
                                        {overView?.avarageRate || 4.9}</div>
                                    <div className="text-xs sm:text-sm text-gray-400">
                                        {t('doctors.stats.rating')}</div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}

                {/* Page Title for our-doctors page */}
                {isOurDoctorsPage && (
                    <motion.div
                        className="text-center mb-12 sm:mb-32"
                        initial={{ opacity: 0, y: -30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="mb-1 goldy text-lg md:text-4xl">
                            {t('doctors.pageTitle').split(' ')[0]} <span className="goldy font-bold">{t('doctors.pageTitle').split(' ').slice(1).join(' ')}</span>
                        </h1>
                        <Text className="text-secondary  text-sm md:text-lg max-w-2xl mx-auto">
                            {t('doctors.pageSubtitle')}
                        </Text>
                    </motion.div>
                )}

                {/* Doctors Grid */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    className={`${isOurDoctorsPage ? "pb-32" : ""}`}
                    animate="visible"
                >
                    <div
                        className={`grid grid-cols-1${isOurDoctorsPage
                            ? "sm:grid-cols-2 lg:grid-cols-3"
                            : "sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
                            } gap-4 md:gap-8`}
                    >
                        {displayedDoctors.map((doctor, index) => (
                            <DoctorCard
                                key={doctor.doctorId}
                                doctor={doctor}
                                index={index}
                                onBookAppointment={handleDoctorBooking}
                            />
                        ))}
                    </div>

                    {/* See More Button */}
                    {shouldShowSeeMore && (
                        <motion.div
                            className="flex justify-center mt-8 sm:mt-12 lg:mt-16"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6, duration: 0.8 }}
                        >
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Button
                                    size="large"
                                    type=""
                                    onClick={() => navigate(`/our-doctors`)}
className="bg-white border-2 border-primary font-semibold py-2 sm:py-3 px-4 sm:px-8 rounded-lg hover:bg-cardColor hover:scale-105 text-primary text-xs sm:text-sm transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-1.5 sm:gap-2 mx-auto"         style={{
                                        boxShadow: '0 20px 40px -10px rgba(255, 255, 255, 0.3)',
                                        backdropFilter: 'blur(10px)'
                                    }}
                                >
                                    <span className="flex items-center justify-center space-x-3 gap-4 relative z-10">
                                        <span className='mx-2'>{t('doctors.viewAll')}</span>
                                        {i18n.language == "ar"
                                            ? <ArrowLeftOutlined className="transition-transform group-hover:translate-x-1" />
                                            : <ArrowRightOutlined className="transition-transform group-hover:translate-x-1" />
                                        }
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                                </Button>
                            </motion.div>
                        </motion.div>
                    )}
                </motion.div>
            </div>

           
 <NeedAuthModal
    open={isModalVisible}
    onConfirm={() => { 
        setIsModalVisible(false); 
        setLoginModal(true);        // ← now works
    }}
    onCancel={() => { 
        setIsModalVisible(false); 
        setPendingDoctor(null); 
        setPendingRoute(null); 
        setIsAppoinments(false); 
    }}
    onGuestBooking={handleGuestBooking}
    isAppoinments={isAppoinments}
/>

   <LoginModal
        loginModal={loginModal}
        setLoginModal={setLoginModal}
      />
    <GuestBookingModal
                open={isGuestModalVisible}
                onCancel={() => {
                    setIsGuestModalVisible(false);
                    setPendingDoctor(null);
                }}
                onSubmit={handleGuestInfoSubmit}
            />
        </section>
    );
}